'use client';

import React from "react";
import {
    Box,
    Flex,
    Spacer,
    useColorModeValue,
    Button,
    Avatar,
    VStack,
    Text,
    HStack,
    useTheme,
    Badge,
} from "@chakra-ui/react";
import { SunIcon, MoonIcon } from "@chakra-ui/icons";
import { LiaBrainSolid } from "react-icons/lia";
import { RiBrain2Fill } from "react-icons/ri";
import { LuBrain } from "react-icons/lu";
import { BiBrain } from "react-icons/bi";
import { css } from "@emotion/react";
import { useColorModeContext } from "../ColorModeContext";
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';

const customSunIconStyle = css`
  circle,
  rect,
  line {
    stroke-width: 2 !important;
  }
`;

const TopBar: React.FC = () => {
    const router = useRouter();
    const pathname = usePathname();
    const theme = useTheme();
    const { user, role, hasPermission } = useAuth();

    const bg = useColorModeValue("perfusionpurple", "#1a202c");
    const avatarColor = useColorModeValue(theme.colors.perfusionpurple as string, "black");
    const { isDarkMode, toggleColorMode } = useColorModeContext();


    // Navigation items with role-based filtering
    const schedulingPermissions = [
        'view:calendar',
        'view:daily_view',
        'view:experiment_design',
        'view:experiment_queue',
        'view:ltec_batches',
        'view:drug_stock',
        'view:surgeon_actions',
        'view:lab_activity',
    ];

    const hasSchedulingAccess = schedulingPermissions.some(permission => hasPermission(permission));

    const navItems = [
        { label: 'Dashboard', href: '/dashboard', permission: 'view:dashboard' },
        { label: 'Samples', href: '/samples', permission: 'view:samples' },
        ...(hasSchedulingAccess ? [{ label: 'Scheduling', href: '/scheduling', permission: 'any' }] : []),
        { label: 'Users', href: '/admin/users', role: 'admin' },
        { label: 'Backend API Docs', href: '/apidocs', role: 'admin' },
    ].filter(item => {
        if (item.permission === 'any') return true; // Already checked for this group
        if (item.permission) return hasPermission(item.permission);
        if (item.role) return role === item.role;
        return true;
    });

    const handleLogout = async () => {
        await signOut(auth);
        router.push('/login');
    };

    return (
        <Box
            bg={bg}
            px={4}
            py={3}
            position="fixed"
            top={0}
            width="100%"
            zIndex={1070}
            height="80px"
            margin={0}
            padding="12px 16px"
            display="flex"
            alignItems="center"
        >
            <Flex align="center" width="100%">
                <VStack
                    align="center"
                    spacing={-10}
                    cursor="pointer"
                    onClick={() => router.push('/dashboard')}
                    _hover={{ opacity: 0.8 }}
                    transition="opacity 0.2s"
                >
                    <Text
                        color="white"
                        fontSize="20px"
                        fontFamily="'Figtree', sans-serif"
                        textTransform="uppercase"
                        position="relative"
                        left="2px"
                    >
                        <Text as="span" fontWeight="500" letterSpacing="-0.01em">
                            xo
                        </Text>
                        <Text as="span" fontWeight="300" letterSpacing="0.05em" ml={1}>
                            digital
                        </Text>
                    </Text>
                    <HStack
                        spacing={0}
                        color="white"
                        fontSize="42px"
                        fontWeight="800"
                        fontFamily="'Figtree', sans-serif"
                        letterSpacing="-0.03em"
                        lineHeight="0.9"
                        textTransform="lowercase"
                        marginTop="-6px"
                        align="center"
                    >
                        {/* <Text
                            color="white"
                            fontSize="42px"
                            fontWeight="800"
                            fontFamily="'Figtree', sans-serif"
                            letterSpacing="-0.03em"
                            lineHeight="0.9"
                            textTransform="lowercase"
                        >
                            c
                        </Text>
                        <Box as={BiBrain}
                            color="white"
                            boxSize="28px"
                            position="relative"
                            top="3px"
                            ml="-2px"
                            mr="-3px"
                            sx={{
                                '& path': {
                                    strokeWidth: '1px'
                                }
                            }}
                        /> */}
                        <Text
                            color="white"
                            fontSize="42px"
                            fontWeight="800"
                            fontFamily="'Figtree', sans-serif"
                            letterSpacing="-0.03em"
                            lineHeight="0.9"
                            textTransform="lowercase"
                        >
                            cortex
                        </Text>
                    </HStack>
                </VStack>

                {/* Navigation Items */}
                {user && (
                    <HStack spacing={4} ml={8}>
                        {navItems.map((item) => {
                            // Special handling for scheduling section
                            const isActive = item.href === '/scheduling'
                                ? pathname.startsWith('/scheduling/')
                                : pathname === item.href || pathname.startsWith(item.href + '/');
                            return (
                                <Link key={item.href} href={item.href}>
                                    <Button
                                        bg={isActive ? "rgba(255, 255, 255, 0.2)" : "transparent"}
                                        color="white"
                                        padding="8px 16px"
                                        height="40px"
                                        borderRadius="6px"
                                        border="1px solid transparent"
                                        fontSize="sm"
                                        fontWeight={isActive ? "semibold" : "medium"}
                                        _hover={{
                                            bg: isActive ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.1)",
                                            borderColor: "rgba(255, 255, 255, 0.3)",
                                        }}
                                        transition="all 0.2s"
                                    >
                                        {item.label}
                                    </Button>
                                </Link>
                            );
                        })}
                    </HStack>
                )}

                <Spacer />
                <HStack spacing={4}>
                    {user && (
                        <VStack spacing={0} align="end">
                            <HStack spacing={2} align="center">
                                <Text fontSize="sm" fontWeight="500" color="white">{user.displayName}</Text>
                                {role && (
                                    <Badge
                                        colorScheme={
                                            role === 'admin' ? 'red' :
                                                role === 'viewer' ? 'green' : 'gray'
                                        }
                                        variant="solid"
                                        fontSize="xs"
                                    >
                                        {role.toUpperCase()}
                                    </Badge>
                                )}
                            </HStack>
                            <Text fontSize="xs" color="gray.300">{user.email}</Text>
                        </VStack>
                    )}
                    {user && (
                        <Avatar
                            size="md"
                            name={user?.displayName || ''}
                            bg="white"
                            color={avatarColor}
                        />
                    )}
                    {user && (
                        <Button
                            onClick={handleLogout}
                            bg="transparent"
                            color="white"
                            padding="10px 16px"
                            height="50px"
                            borderRadius="8px"
                            border="2px solid transparent"
                            fontSize="sm"
                            fontWeight="medium"
                            _hover={{
                                borderColor: "rgba(200, 200, 200, 0.75)",
                            }}
                        >
                            Sign Out
                        </Button>
                    )}
                    <Button
                        onClick={toggleColorMode}
                        bg="transparent"
                        padding="10px"
                        width="50px"
                        height="50px"
                        borderRadius="50%"
                        borderColor="rgba(200, 200, 200, 0.75)"
                        mr="1"
                        _hover={{
                            border: "2px",
                            borderColor: "rgba(200, 200, 200, 0.75)",
                            padding: "8px",
                        }}
                    >
                        {isDarkMode ? (
                            <SunIcon boxSize={6} css={customSunIconStyle} />
                        ) : (
                            <MoonIcon boxSize={6} color="white" />
                        )}
                    </Button>
                </HStack>
            </Flex>
        </Box>
    );
};

export default TopBar; 